(function () {
  const selectors = {
    radioGroup: '[role="radiogroup"]',
    radioInput: 'input[type="radio"]',
  };

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function getInputLabel(input) {
    const ariaLabel = input.getAttribute('aria-label');
    if (ariaLabel) {
      return ariaLabel;
    }

    if (input.id) {
      const label = input.labels && input.labels.length ? input.labels[0] : null;
      if (label) {
        return label.textContent;
      }
    }

    return '';
  }

  function setNativeChecked(input) {
    const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'checked');

    if (descriptor && typeof descriptor.set === 'function') {
      descriptor.set.call(input, true);
    } else {
      input.checked = true;
    }
  }

  function notifyRadioChange(input) {
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  class KlaviyoFooterController extends HTMLElement {
    constructor() {
      super();

      this.isInitialized = false;
      this.isApplyingDefaultSelection = false;
      this.hasUserSelected = false;
      this.observer = null;
      this.pendingTimers = new Set();
      this.handleKlaviyoFormsEvent = this.handleKlaviyoFormsEvent.bind(this);
      this.handleMutation = this.handleMutation.bind(this);
      this.handleRadioChange = this.handleRadioChange.bind(this);
      this.applyDefaultSelection = this.applyDefaultSelection.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) {
        return;
      }

      this.isInitialized = true;
      window.addEventListener('klaviyoForms', this.handleKlaviyoFormsEvent);
      this.addEventListener('change', this.handleRadioChange, true);

      if ('MutationObserver' in window) {
        this.observer = new MutationObserver(this.handleMutation);
        this.observer.observe(this, {
          childList: true,
          subtree: true,
        });
      }

      this.scheduleDefaultSelection();
      this.scheduleDefaultSelection(250);
      this.scheduleDefaultSelection(1000);
      this.scheduleDefaultSelection(2500);
    }

    disconnectedCallback() {
      window.removeEventListener('klaviyoForms', this.handleKlaviyoFormsEvent);
      this.removeEventListener('change', this.handleRadioChange, true);

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      this.pendingTimers.forEach((timer) => window.clearTimeout(timer));
      this.pendingTimers.clear();

      this.isInitialized = false;
      this.hasUserSelected = false;
      this.isApplyingDefaultSelection = false;
    }

    get defaultAudienceOption() {
      return normalize(this.dataset.defaultAudienceOption || 'Both');
    }

    handleKlaviyoFormsEvent() {
      this.scheduleDefaultSelection();
    }

    handleMutation() {
      this.scheduleDefaultSelection();
    }

    handleRadioChange(event) {
      if (this.isApplyingDefaultSelection) {
        return;
      }

      if (event.target && event.target.matches(selectors.radioInput)) {
        this.hasUserSelected = true;
      }
    }

    scheduleDefaultSelection(delay = 0) {
      const timer = window.setTimeout(() => {
        this.pendingTimers.delete(timer);
        this.applyDefaultSelection();
      }, delay);

      this.pendingTimers.add(timer);
    }

    applyDefaultSelection() {
      if (this.hasUserSelected) {
        return;
      }

      const radioGroups = this.querySelectorAll(selectors.radioGroup);

      radioGroups.forEach((radioGroup) => {
        const inputs = Array.from(radioGroup.querySelectorAll(selectors.radioInput));

        if (!inputs.length) {
          return;
        }

        const defaultInput = inputs.find((input) => {
          return normalize(getInputLabel(input)) === this.defaultAudienceOption;
        });

        if (!defaultInput || defaultInput.disabled) {
          return;
        }

        if (defaultInput.checked) {
          return;
        }

        this.isApplyingDefaultSelection = true;

        try {
          defaultInput.click();

          if (!defaultInput.checked) {
            setNativeChecked(defaultInput);
            notifyRadioChange(defaultInput);
          }
        } finally {
          this.isApplyingDefaultSelection = false;
        }
      });
    }
  }

  if (!customElements.get('klaviyo-footer-controller')) {
    customElements.define('klaviyo-footer-controller', KlaviyoFooterController);
  }
})();
